<template>
  <div class="payment-transactions-page">
    <div class="page-header">
      <div>
        <h1 class="page-title">{{ $t('admin.payment_transactions.title') }}</h1>
        <p class="page-subtitle">{{ $t('admin.payment_transactions.subtitle') }}</p>
      </div>
    </div>

    <!-- Provider Statistics -->
    <div class="stats-section">
      <div class="stats-header">
        <h2 class="stats-title">{{ $t('admin.payment_transactions.provider_performance') }}</h2>
        <select v-model="statsPeriod" class="period-select" @change="fetchStats">
          <option value="today">{{ $t('admin.payment_transactions.period_today') }}</option>
          <option value="7d">{{ $t('admin.payment_transactions.period_7d') }}</option>
          <option value="30d">{{ $t('admin.payment_transactions.period_30d') }}</option>
          <option value="90d">{{ $t('admin.payment_transactions.period_90d') }}</option>
          <option value="all">{{ $t('admin.payment_transactions.period_all') }}</option>
        </select>
      </div>

      <div v-if="statsLoading" class="stats-loading">
        <span class="spinner"></span>
        {{ $t('admin.payment_transactions.loading_stats') }}
      </div>

      <div v-else-if="providerStats.length === 0" class="stats-empty">
        {{ $t('admin.payment_transactions.no_stats') }}
      </div>

      <div v-else class="provider-cards">
        <div v-for="provider in providerStats" :key="provider.payment_method" class="provider-card">
          <div class="provider-card-header">
            <span class="provider-name">{{ formatMethod(provider.payment_method) }}</span>
            <span
              class="success-rate"
              :class="successRateClass(provider.success_rate_percent)"
            >{{ provider.success_rate_percent }}%</span>
          </div>

          <div class="provider-stats-grid">
            <div class="provider-stat">
              <div class="provider-stat-value">{{ provider.total_transactions.toLocaleString() }}</div>
              <div class="provider-stat-label">{{ $t('admin.payment_transactions.stat_transactions') }}</div>
            </div>
            <div class="provider-stat">
              <div class="provider-stat-value stat-success">{{ provider.successful_transactions.toLocaleString() }}</div>
              <div class="provider-stat-label">{{ $t('admin.payment_transactions.stat_successful') }}</div>
            </div>
            <div class="provider-stat">
              <div class="provider-stat-value stat-danger">{{ provider.failed_transactions.toLocaleString() }}</div>
              <div class="provider-stat-label">{{ $t('admin.payment_transactions.stat_failed') }}</div>
            </div>
            <div class="provider-stat">
              <div class="provider-stat-value">{{ formatCents(provider.total_volume) }}</div>
              <div class="provider-stat-label">{{ $t('admin.payment_transactions.stat_volume') }}</div>
            </div>
            <div class="provider-stat">
              <div class="provider-stat-value">{{ formatCents(provider.total_fees) }}</div>
              <div class="provider-stat-label">{{ $t('admin.payment_transactions.stat_fees') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-row">
      <select v-model="filters.payment_method" class="filter-select" @change="fetchTransactions(1)">
        <option value="">{{ $t('admin.payment_transactions.filter_all_providers') }}</option>
        <option value="card">{{ $t('admin.payment_transactions.filter_card') }}</option>
        <option value="crypto">{{ $t('admin.payment_transactions.filter_crypto') }}</option>
        <option value="manual">{{ $t('admin.payment_transactions.filter_manual') }}</option>
      </select>

      <select v-model="filters.status" class="filter-select" @change="fetchTransactions(1)">
        <option value="">{{ $t('admin.payment_transactions.filter_all_statuses') }}</option>
        <option value="completed">{{ $t('admin.payment_transactions.filter_completed') }}</option>
        <option value="pending">{{ $t('admin.payment_transactions.filter_pending') }}</option>
        <option value="failed">{{ $t('admin.payment_transactions.filter_failed') }}</option>
        <option value="cancelled">{{ $t('admin.payment_transactions.filter_cancelled') }}</option>
      </select>

      <input
        v-model="filters.from"
        type="date"
        class="filter-input"
        @change="fetchTransactions(1)"
      >
      <input
        v-model="filters.to"
        type="date"
        class="filter-input"
        @change="fetchTransactions(1)"
      >

      <div class="search-wrap">
        <input
          v-model="filters.search"
          type="text"
          class="filter-input search-input"
          :placeholder="$t('admin.payment_transactions.filter_search_player')"
          @keyup.enter="fetchTransactions(1)"
        >
        <button class="btn-search" @click="fetchTransactions(1)">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Table -->
    <div class="table-container">
      <div v-if="loading" class="table-loading">
        <span class="spinner"></span>
        {{ $t('admin.payment_transactions.loading_transactions') }}
      </div>

      <template v-else>
        <table>
          <thead>
            <tr>
              <th>{{ $t('admin.payment_transactions.col_id') }}</th>
              <th>{{ $t('admin.payment_transactions.col_player') }}</th>
              <th>{{ $t('admin.payment_transactions.col_type') }}</th>
              <th>{{ $t('admin.payment_transactions.col_amount') }}</th>
              <th>{{ $t('admin.payment_transactions.col_currency') }}</th>
              <th>{{ $t('admin.payment_transactions.col_status') }}</th>
              <th>{{ $t('admin.payment_transactions.col_provider') }}</th>
              <th>{{ $t('admin.payment_transactions.col_external_id') }}</th>
              <th>{{ $t('admin.payment_transactions.col_date') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="transactions.length === 0">
              <td colspan="9" class="empty-row">{{ $t('admin.payment_transactions.no_transactions') }}</td>
            </tr>
            <tr v-for="tx in transactions" :key="tx.id">
              <td class="td-id">{{ tx.id }}</td>
              <td>
                <div class="player-cell">
                  <span class="player-username">{{ tx.user.username }}</span>
                  <span class="player-email">{{ tx.user.email }}</span>
                </div>
              </td>
              <td>
                <span class="badge" :class="'badge-type-' + tx.type">{{ tx.type }}</span>
              </td>
              <td>
                <span class="amount-value" :class="tx.amount >= 0 ? 'amount-positive' : 'amount-negative'">
                  {{ formatCents(tx.amount) }}
                </span>
              </td>
              <td>{{ tx.currency }}</td>
              <td>
                <span class="badge" :class="'badge-' + tx.status">{{ tx.status }}</span>
              </td>
              <td>
                <span class="method-badge">{{ formatMethod(tx.payment_method) }}</span>
              </td>
              <td class="td-external-id">
                <span v-if="tx.external_tx_id" :title="tx.external_tx_id">{{ truncateId(tx.external_tx_id) }}</span>
                <span v-else class="text-muted">&mdash;</span>
              </td>
              <td class="td-date">{{ formatDate(tx.created_at) }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Pagination -->
        <div v-if="meta.last_page > 1" class="pagination">
          <button class="pagination-btn" :disabled="meta.current_page <= 1" @click="fetchTransactions(meta.current_page - 1)">&larr;</button>
          <button
            v-for="page in buildPageRange(meta.current_page, meta.last_page)"
            :key="page"
            class="pagination-btn"
            :class="{ active: page === meta.current_page }"
            @click="fetchTransactions(page)"
          >
            {{ page }}
          </button>
          <span class="pagination-info">
            {{ (meta.current_page - 1) * meta.per_page + 1 }}-{{ Math.min(meta.current_page * meta.per_page, meta.total) }} of {{ meta.total }}
          </span>
          <button class="pagination-btn" :disabled="meta.current_page >= meta.last_page" @click="fetchTransactions(meta.current_page + 1)">&rarr;</button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { useToastStore } from '@/stores/toast'

interface PaymentTransaction {
  id: number
  user: { id: number; username: string; email: string }
  type: string
  amount: number
  currency: string
  status: string
  payment_method: string
  external_tx_id: string | null
  created_at: string
}

interface PaginationMeta {
  current_page: number
  last_page: number
  per_page: number
  total: number
}

interface ProviderStat {
  payment_method: string
  total_transactions: number
  successful_transactions: number
  failed_transactions: number
  success_rate_percent: number
  total_volume: number
  total_fees: number
}

const { t } = useI18n()
const toast = useToastStore()

const providerStats = ref<ProviderStat[]>([])
const statsLoading = ref(false)
const statsPeriod = ref('30d')

const transactions = ref<PaymentTransaction[]>([])
const loading = ref(false)
const meta = ref<PaginationMeta>({ current_page: 1, last_page: 1, per_page: 25, total: 0 })
const filters = ref({
  payment_method: '',
  status: '',
  from: '',
  to: '',
  search: '',
})

async function fetchStats() {
  statsLoading.value = true
  try {
    const response = await apiClient.get('/admin/api/payment-stats', {
      params: { period: statsPeriod.value },
    })
    providerStats.value = response.data.providers
  } catch {
    toast.error(t('admin.payment_transactions.toast_stats_failed'))
  } finally {
    statsLoading.value = false
  }
}

function successRateClass(rate: number): string {
  if (rate >= 95) return 'rate-green'
  if (rate >= 85) return 'rate-yellow'
  return 'rate-red'
}

function formatCents(cents: number): string {
  const dollars = cents / 100
  const sign = dollars < 0 ? '-' : ''
  return sign + '$' + Math.abs(dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function formatMethod(method: string | null): string {
  if (!method) return '—'
  return method.charAt(0).toUpperCase() + method.slice(1)
}

function truncateId(id: string): string {
  if (id.length <= 20) return id
  return id.slice(0, 10) + '...' + id.slice(-6)
}

function buildPageRange(current: number, total: number): number[] {
  const pages: number[] = []
  const start = Math.max(1, current - 2)
  const end = Math.min(total, current + 2)
  for (let i = start; i <= end; i++) {
    pages.push(i)
  }
  return pages
}

async function fetchTransactions(page: number) {
  loading.value = true
  try {
    const params: Record<string, string | number> = { page }
    if (filters.value.payment_method) params.payment_method = filters.value.payment_method
    if (filters.value.status) params.status = filters.value.status
    if (filters.value.from) params.from = filters.value.from
    if (filters.value.to) params.to = filters.value.to
    if (filters.value.search) params.search = filters.value.search

    const response = await apiClient.get('/admin/api/payment-transactions', { params })
    transactions.value = response.data.data
    meta.value = {
      current_page: response.data.current_page,
      last_page: response.data.last_page,
      per_page: response.data.per_page,
      total: response.data.total,
    }
  } catch {
    toast.error(t('admin.payment_transactions.toast_tx_failed'))
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchStats()
  fetchTransactions(1)
})
</script>

<style scoped>
.payment-transactions-page {
  padding: 24px;
  animation: fadeIn 0.3s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Page Header */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}

.page-title {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin: 0 0 4px;
}

.page-subtitle {
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0;
}

/* Filters */
.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select,
.filter-input {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 9px 12px;
  font-size: 13px;
  font-family: inherit;
}

.filter-select option {
  background: var(--bg-secondary, #1a2c38);
}

.filter-input:focus,
.filter-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.filter-input[type="date"] {
  width: 150px;
  color-scheme: dark;
}

.search-wrap {
  display: flex;
  gap: 0;
}

.search-input {
  border-radius: var(--radius-md, 8px) 0 0 var(--radius-md, 8px);
  width: 200px;
}

.btn-search {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 12px;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-left: none;
  border-radius: 0 var(--radius-md, 8px) var(--radius-md, 8px) 0;
  color: var(--text-secondary, #b1bad3);
  cursor: pointer;
  transition: color 150ms ease;
}

.btn-search:hover {
  color: var(--accent-primary, #00e701);
}

/* Table */
.table-container {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  overflow: hidden;
  margin-bottom: 16px;
}

.table-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 48px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: var(--bg-tertiary, #213743);
}

th {
  padding: 12px 16px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  white-space: nowrap;
}

td {
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
  font-size: 13px;
  color: var(--text-secondary, #b1bad3);
}

tbody tr {
  background: var(--bg-secondary, #1a2c38);
  transition: background 150ms ease;
}

tbody tr:hover {
  background: #253d4f;
}

tbody tr:last-child td {
  border-bottom: none;
}

.td-id {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Player Cell */
.player-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.player-username {
  font-weight: 600;
  color: var(--text-primary, #fff);
  font-size: 13px;
}

.player-email {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Amount */
.amount-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-weight: 700;
}

.amount-positive {
  color: var(--color-success, #00e701);
}

.amount-negative {
  color: var(--color-danger, #ed4245);
}

/* Method Badge */
.method-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(255, 215, 0, 0.1);
  color: var(--text-secondary, #b1bad3);
  border-radius: var(--radius-full, 9999px);
  font-size: 12px;
}

/* Badges */
.badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.badge-completed {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-pending {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.badge-failed {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.badge-cancelled {
  background: rgba(127, 143, 164, 0.15);
  color: var(--text-tertiary, #7f8fa4);
}

.badge-type-deposit {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.badge-type-withdrawal {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.td-external-id {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
}

.td-date {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  white-space: nowrap;
}

.text-muted {
  color: var(--text-tertiary, #7f8fa4);
}

/* Empty Row */
.empty-row {
  text-align: center;
  padding: 48px 20px !important;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
}

/* Pagination */
.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.pagination-btn {
  width: 32px;
  height: 32px;
  border-radius: var(--radius-md, 8px);
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 150ms ease;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.pagination-btn:hover:not(:disabled):not(.active) {
  color: var(--text-primary, #fff);
  border-color: rgba(255, 255, 255, 0.2);
}

.pagination-btn.active {
  background: var(--accent-primary, #00e701);
  color: #000;
  border-color: var(--accent-primary, #00e701);
}

.pagination-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.pagination-info {
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  margin: 0 12px;
}

/* Spinner */
.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Stats Section */
.stats-section {
  margin-bottom: 24px;
}

.stats-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.stats-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary, #fff);
  margin: 0;
}

.period-select {
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: var(--radius-md, 8px);
  color: var(--text-primary, #fff);
  padding: 8px 12px;
  font-size: 13px;
  font-family: inherit;
  cursor: pointer;
}

.period-select option {
  background: var(--bg-secondary, #1a2c38);
}

.period-select:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.stats-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 20px;
  color: var(--text-secondary, #b1bad3);
  font-size: 14px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
}

.stats-empty {
  padding: 32px 20px;
  text-align: center;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 14px;
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
}

.provider-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.provider-card {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: var(--radius-lg, 12px);
  padding: 20px;
  transition: all 150ms ease;
}

.provider-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  border-color: rgba(255, 255, 255, 0.1);
}

.provider-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.provider-name {
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.success-rate {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 14px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: var(--radius-full, 9999px);
}

.rate-green {
  background: rgba(0, 231, 1, 0.15);
  color: var(--color-success, #00e701);
}

.rate-yellow {
  background: rgba(255, 167, 38, 0.15);
  color: var(--color-warning, #ffa726);
}

.rate-red {
  background: rgba(237, 66, 69, 0.15);
  color: var(--color-danger, #ed4245);
}

.provider-stats-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
}

.provider-stat {
  text-align: center;
}

.provider-stat-value {
  font-family: var(--font-family-mono, 'JetBrains Mono', monospace);
  font-size: 16px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  margin-bottom: 4px;
}

.provider-stat-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stat-success {
  color: var(--color-success, #00e701);
}

.stat-danger {
  color: var(--color-danger, #ed4245);
}

/* Responsive */
@media (max-width: 768px) {
  .payment-transactions-page {
    padding: 16px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .filters-row {
    flex-direction: column;
  }

  .search-input {
    width: 100%;
  }

  .provider-cards {
    grid-template-columns: 1fr;
  }

  .provider-stats-grid {
    grid-template-columns: repeat(3, 1fr);
    row-gap: 16px;
  }
}
</style>
